import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Save } from "lucide-react";

export default function AIAgent() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/ai-agent").then(r => setCfg(r.data)); }, []);
  if (!cfg) return <div className="p-8 text-slate-500">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ai-agent", cfg);
      toast.success("AI Agent updated");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const setQ = (i, v) => {
    const arr = [...(cfg.qualification_questions || [])]; arr[i] = v;
    setCfg({ ...cfg, qualification_questions: arr });
  };
  const addQ = () => setCfg({ ...cfg, qualification_questions: [...(cfg.qualification_questions||[]), ""] });
  const rmQ = (i) => setCfg({ ...cfg, qualification_questions: cfg.qualification_questions.filter((_,x)=>x!==i) });

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Sparkles className="w-6 h-6 text-indigo-600"/>AI Sales Agent</h1>
          <p className="text-sm text-slate-500">Configure how Aria talks to your customers on WhatsApp</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-ai-agent-button">
          <Save className="w-4 h-4 mr-1"/>{saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <Card className="p-6 border-slate-200 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Agent name</Label><Input value={cfg.agent_name||""} onChange={(e)=>setCfg({...cfg, agent_name: e.target.value})} data-testid="agent-name-input"/></div>
          <div><Label>Business name</Label><Input value={cfg.business_name||""} onChange={(e)=>setCfg({...cfg, business_name: e.target.value})} data-testid="business-name-input"/></div>
        </div>
        <div><Label>Business description</Label><Textarea rows={2} value={cfg.business_description||""} onChange={(e)=>setCfg({...cfg, business_description: e.target.value})} data-testid="business-description-input"/></div>
        <div><Label>Tone & personality</Label><Input value={cfg.tone||""} onChange={(e)=>setCfg({...cfg, tone: e.target.value})} data-testid="tone-input"/></div>
      </Card>

      <Card className="p-6 border-slate-200 space-y-4">
        <div className="font-semibold text-slate-900">Products, Pricing & Locations</div>
        <div><Label>Products / services</Label><Textarea rows={4} value={cfg.products||""} onChange={(e)=>setCfg({...cfg, products: e.target.value})} data-testid="products-input"/></div>
        <div><Label>Pricing <span className="text-xs text-rose-600">(AI will only quote what is listed here)</span></Label><Textarea rows={4} value={cfg.pricing||""} onChange={(e)=>setCfg({...cfg, pricing: e.target.value})} data-testid="pricing-input"/></div>
        <div><Label>Locations</Label><Input value={cfg.locations||""} onChange={(e)=>setCfg({...cfg, locations: e.target.value})} data-testid="locations-input"/></div>
        <div><Label>Working hours</Label><Input value={cfg.working_hours||""} onChange={(e)=>setCfg({...cfg, working_hours: e.target.value})}/></div>
      </Card>

      <Card className="p-6 border-slate-200 space-y-3">
        <div className="font-semibold text-slate-900">FAQs</div>
        <Textarea rows={6} value={cfg.faqs||""} onChange={(e)=>setCfg({...cfg, faqs: e.target.value})} placeholder="Q: ... A: ..." data-testid="faqs-input"/>
      </Card>

      <Card className="p-6 border-slate-200 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-slate-900">Qualification Questions</div>
          <Button size="sm" variant="outline" onClick={addQ} data-testid="add-question-button">+ Add</Button>
        </div>
        <p className="text-xs text-slate-500">Aria will ask these progressively to qualify leads.</p>
        <div className="space-y-2">
          {(cfg.qualification_questions||[]).map((q, i) => (
            <div key={i} className="flex gap-2">
              <Input value={q} onChange={(e)=>setQ(i, e.target.value)} data-testid={`qual-q-${i}`}/>
              <Button variant="outline" size="sm" onClick={()=>rmQ(i)} data-testid={`remove-q-${i}`}>×</Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-slate-200 space-y-3">
        <div className="font-semibold text-slate-900">Handover Rules</div>
        <Textarea rows={3} value={cfg.handover_rules||""} onChange={(e)=>setCfg({...cfg, handover_rules: e.target.value})} data-testid="handover-rules-input"/>
      </Card>
    </div>
  );
}
